import { memo, type ReactElement } from 'react';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { DeleteSuiteDialog, EditSuiteDialog } from '@/features/suites/components/dialogs';
import { useSuitesTableController } from '@/features/suites/components/useSuitesTableController';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { SuitesTableProps } from '@/features/suites/components/SuitesTable.types';
import type { SuiteListItemDto } from '@flowqa/shared';

const SuitesTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  hideFooter,
  disableColumnSorting = true,
  disableColumnMenu = true,
  showToolbar = true,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'suite' }),
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  initialData,
  onRowsChange,
  onQueryMeta,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
  isAuthenticatedOverride,
  disableLinks = false,
  disableSummaryLinks = false,
  search,
  sparklineRangeDays = 7,
  metricsStartDay,
  metricsEndDay,
  metricsBatchDelayMs = 0,
  blockOnMetricsBatch: _blockOnMetricsBatch = true,
  enableMetricsBatch = true,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  metricSeriesBySuiteIdOverride,
  tagColorByNameOverride,
  tagOptionsOverride,
  hideActionsColumn = false,
  showPaginationSkeletonWhenRowsVisible = false,
  preservePreviousData = false,
  dynamicRowHeight = true,
  rowHeight,
  active = true,
}: SuitesTableProps): ReactElement => {
  const resolvedRowHeight = rowHeight ?? TABLE_ROW_HEIGHT;
  const resolvedEstimatedRowHeight = rowHeight ?? TABLE_ROW_MEASURE_HEIGHT;
  const {
    columns,
    deleteLoading,
    deleteTarget,
    editEditor,
    editErrors,
    editForm,
    editTarget,
    gridConfig,
    handleEditClose,
    handleEditSave,
    handleRowsChange,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  } = useSuitesTableController({
    disableLinks,
    disableSummaryLinks,
    enableMetricsBatch,
    fixedFilters,
    hideActionsColumn,
    initialFilterModel,
    initialSortModel,
    isAuthenticatedOverride,
    metricSeriesBySuiteIdOverride,
    metricsBatchDelayMs,
    metricsEndDay,
    metricsStartDay,
    onRowsChange,
    onStatusSelect,
    persistKey,
    queryFn,
    queryKey,
    showDeltas,
    sparklineRangeDays,
    syncStateToUrl,
    tagColorByNameOverride,
    tagOptionsOverride,
  });

  return (
    <>
      <ServerDataTable<SuiteListItemDto>
        queryPath="suites/query"
        {...gridConfig}
        queryFn={resolvedQueryFn}
        queryKey={resolvedQueryKey}
        initialData={initialData}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={isAuthenticated}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        pageSizeOptions={pageSizeOptions}
        initialPageSize={initialPageSize}
        search={search}
        columnVisibilityModel={columnVisibilityModel}
        disablePageSizeSelect={disablePageSizeSelect}
        hideFooter={hideFooter}
        disableColumnMenu={disableColumnMenu}
        disableColumnSorting={disableColumnSorting}
        showToolbar={showToolbar}
        showColumnVerticalBorder={false}
        showCellVerticalBorder={false}
        noRowsText={noRowsText}
        interactionMode="static"
        getRowHeight={dynamicRowHeight ? () => 'auto' : undefined}
        skeletonRowHeight={resolvedRowHeight}
        skeletonGetRowHeight={dynamicRowHeight ? undefined : null}
        showSkeletonOnFetch={showPaginationSkeletonWhenRowsVisible}
        showPaginationSkeletonWhenRowsVisible={showPaginationSkeletonWhenRowsVisible}
        preservePreviousData={preservePreviousData}
        active={active}
        showLastUpdated={showLastUpdated}
        autoHeight
        onRowsChange={handleRowsChange}
        onQueryMeta={onQueryMeta}
        onQueryInput={onQueryInput}
        onDataUpdatedAt={onDataUpdatedAt}
        gridProps={{
          getEstimatedRowHeight: dynamicRowHeight ? () => resolvedEstimatedRowHeight : undefined,
          rowHeight: dynamicRowHeight ? undefined : resolvedRowHeight,
          onColumnVisibilityModelChange,
          slotProps: {
            toolbar: {
              showQuickFilter: true,
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={tableSx}
      />
      <DeleteSuiteDialog
        open={deleteTarget !== null}
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <EditSuiteDialog
        open={editTarget !== null}
        saving={editEditor.isPending}
        values={editForm.values}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        errorMessage={editForm.submitError}
        onClose={handleEditClose}
        onSave={handleEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
        }}
      />
    </>
  );
};

export const SuitesTable = memo(SuitesTableComponent);
